function initSpiCartDrawer() {
  const root = document.getElementById("spi_cart-drawer");
  const overlay = document.getElementById("spi_cart-drawer-overlay");
  const closeBtn = document.getElementById("spi_cart-drawer-close");
  const shipping = document.getElementById("spi_cart-drawer-shipping");
  const body = document.getElementById("spi_cart-drawer-body");
  const footer = document.getElementById("spi_cart-drawer-footer");

  if (!root || !body || !footer) return;

  let i18n = {};
  try {
    i18n = JSON.parse(root.dataset.i18n || "{}");
  } catch (err) {
    i18n = {};
  }

  const opts = {
    i18n: i18n,
    showVendor: root.dataset.showVendor === "true",
    currencyCodeEnabled: root.dataset.currencyCodeEnabled === "true",
    moneyFormat: root.dataset.moneyFormat,
    cartUrl: root.dataset.cartUrl || window.routes.cart_url,
    rootUrl: root.dataset.rootUrl || "/",
    showShippingBar: root.dataset.showShippingBar === "true",
    freeShippingThreshold: parseInt(root.dataset.freeShippingThreshold, 10) || 0,
    enableCartAddon: root.dataset.enableCartAddon === "true",
    showPromoCode: root.dataset.showPromoCode === "true",
    showCartNote: root.dataset.showCartNote === "true"
  };

  function isDrawerMode() {
    return root.dataset.cartType === "drawer";
  }

  // The promo code row is collapsible; null means "no manual preference
  // yet", so renderPromoCode() picks a sensible default (open if a code is
  // already applied, closed otherwise). Once the shopper toggles it, that
  // choice sticks across refreshDrawer() re-renders until the drawer closes.
  let promoExpandedByUser = null;

  function openDrawer(skipRefresh) {
    // Pull the latest cart by default, so external callers that added an
    // item via their own fetch(window.routes.cart_add_url + '.js') (product cards, quick view)
    // don't open a stale, "empty" drawer — window.SpiCart.open relies on
    // this. Callers that already know the drawer's DOM is current (the
    // header cart icon, which didn't just change anything; addToCart()
    // below, which already refreshed itself) pass skipRefresh so opening
    // doesn't re-fetch and rebuild the whole footer for nothing — that
    // redundant rebuild, landing a beat after the drawer is already
    // visible, is what shows up as a "blink" on open.
    if (!skipRefresh) {
      refreshDrawer();
    }
    root.classList.add("spi_active");
    if (overlay) overlay.classList.add("spi_active");
    document.documentElement.style.overflow = "hidden";
  }

  function closeDrawer() {
    root.classList.remove("spi_active");
    if (overlay) overlay.classList.remove("spi_active");
    document.documentElement.style.overflow = "";
  }

  function escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  // Promo/coupon message templates carry a literal "{{ code }}" placeholder
  // (the code isn't known until the shopper types one, so it can't be filled
  // server-side the way e.g. the shipping bar's {{ amount }} is) — fill it
  // in here with whatever they actually entered.
  function fillPromoMessage(template, code) {
    return (template || "").replace(/\{\{\s*code\s*\}\}/, code);
  }

  function formatMoney(cents, format) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === "undefined" ? 2 : precision;
      thousands = typeof thousands === "undefined" ? "," : thousands;
      decimal = typeof decimal === "undefined" ? "." : decimal;

      if (isNaN(number) || number == null) return "0";

      number = (number / 100.0).toFixed(precision);

      const parts = number.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : "";

      return dollars + centsPart;
    }

    const match = formatString.match(placeholderRegex);
    let value = "";

    switch (match ? match[1] : "amount") {
      case "amount_no_decimals":
        value = withDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = withDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = withDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_space_separator":
        value = withDelimiters(cents, 2, " ", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = withDelimiters(cents, 0, " ", "");
        break;
      case "amount_with_apostrophe_separator":
        value = withDelimiters(cents, 2, "'", ".");
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return match ? formatString.replace(placeholderRegex, value) : formatString + value;
  }

  function formatPrice(cents, currencyCode) {
    const formatted = formatMoney(cents, opts.moneyFormat);
    return opts.currencyCodeEnabled ? formatted + " " + currencyCode : formatted;
  }

  function renderItem(item, currencyCode) {
    const vendorHtml = opts.showVendor && item.vendor
      ? '<p class="spi_cart-drawer-item-vendor">' + escapeHtml(item.vendor) + "</p>"
      : "";
    const variantHtml = !item.product_has_only_default_variant && item.variant_title
      ? '<p class="spi_cart-drawer-item-variant">' + escapeHtml(item.variant_title) + "</p>"
      : "";
    const sellingPlanHtml = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? '<p class="spi_cart-drawer-item-selling-plan">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + "</p>"
      : "";
    const unitPriceHtml = item.unit_price_measurement
      ? '<p class="spi_cart-drawer-item-unit-price">' + formatPrice(item.unit_price, currencyCode) + '/' + (item.unit_price_measurement.reference_value != 1 ? item.unit_price_measurement.reference_value : '') + item.unit_price_measurement.reference_unit + "</p>"
      : "";
    // Product-level (automatic) discounts: struck original line price + each discount's name.
    const originalPriceHtml = item.original_line_price > item.final_line_price
      ? ' <s class="spi_cart-drawer-item-original-price">' + formatPrice(item.original_line_price, currencyCode) + "</s>"
      : "";
    const lineDiscountsHtml = (item.line_level_discount_allocations || []).map(function (allocation) {
      const title = allocation.discount_application && allocation.discount_application.title;
      return '<p class="spi_cart-drawer-item-discount">' + escapeHtml(title || "") +
        " (-" + formatMoney(allocation.amount, opts.moneyFormat) + ")</p>";
    }).join("");
    const imageUrl = item.image
      ? item.image + (item.image.indexOf("?") === -1 ? "?" : "&") + "width=180"
      : "";
    const imageHtml = item.image
      ? '<img src="' + escapeHtml(imageUrl) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">'
      : "";

    return (
      '<li class="spi_cart-drawer-item" data-line-id="' + item.key + '">' +
      '<a href="' + item.url + '" class="spi_cart-drawer-item-image">' + imageHtml + "</a>" +
      '<div class="spi_cart-drawer-item-details">' +
      vendorHtml +
      '<a href="' + item.url + '" class="spi_cart-drawer-item-name">' + escapeHtml(item.product_title) + "</a>" +
      variantHtml +
      sellingPlanHtml +
      '<p class="spi_cart-drawer-item-price">' + formatPrice(item.final_line_price, currencyCode) + originalPriceHtml + "</p>" +
      lineDiscountsHtml +
      unitPriceHtml +
      '<div class="spi_cart-drawer-item-actions">' +
      '<div class="spi_cart-drawer-qty">' +
      '<button type="button" class="spi_cart-drawer-qty-btn" data-qty-decrease aria-label="' + escapeHtml(opts.i18n.decreaseQuantity) + '">&minus;</button>' +
      '<span class="spi_cart-drawer-qty-value" data-qty-value>' + item.quantity + "</span>" +
      '<button type="button" class="spi_cart-drawer-qty-btn" data-qty-increase aria-label="' + escapeHtml(opts.i18n.increaseQuantity) + '">&plus;</button>' +
      "</div>" +
      '<button type="button" class="spi_cart-drawer-remove-btn" data-line-id="' + item.key + '">' + escapeHtml(opts.i18n.remove) + "</button>" +
      "</div>" +
      "</div>" +
      "</li>"
    );
  }

  function renderBody(cart) {
    if (cart.item_count === 0) {
      return (
        '<div class="spi_cart-drawer-empty">' +
        '<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5"><g transform="matrix(1.2,0,0,1.2,-6.394,-6.39)"><path d="M55.5 39.6c-1-5.7-2-11.5-3-17.2-.3-1.8-.6-3.7-1-5.5-.6-3.4-3.2-6.3-6.7-6.9-1.7-.3-3.5-.2-5.1-.2H22.6c-2.4 0-4.6.1-6.7 1.5-1.8 1.3-3 3.1-3.4 5.3C11 25.2 9.4 33.8 8 42.3c-.7 4.1 1.1 8.3 4.7 10.4 2 1.2 4.2 1.4 6.4 1.4h24.4c1.7 0 3.4.1 5-.3 4.2-1 7.2-4.8 7.5-9.1.3-1.6-.2-3.4-.5-5.1z"></path><path d="M38.5 17.6v3.7c-.1 2.1-1.2 4.2-3.1 5.3-3.9 2.4-9.2 0-9.9-4.6-.2-1.4-.1-2.9-.1-4.3"></path></g></svg>' +
        '<h3 class="spi_cart-drawer-empty-title">' + escapeHtml(opts.i18n.emptyTitle) + "</h3>" +
        '<p class="spi_cart-drawer-empty-subtext">' + escapeHtml(opts.i18n.emptySubtext) + "</p>" +
        "</div>"
      );
    }
    const itemsHtml = cart.items.map(function (item) {
      return renderItem(item, cart.currency);
    }).join("");
    return '<ul class="spi_cart-drawer-items">' + itemsHtml + "</ul>";
  }

  function renderShippingBar(cart) {
    if (!opts.showShippingBar || opts.freeShippingThreshold <= 0 || cart.item_count === 0) return "";

    const currentCents = Math.min(cart.total_price, opts.freeShippingThreshold);
    const remainingCents = Math.max(0, opts.freeShippingThreshold - cart.total_price);
    const percent = Math.min(100, (currentCents / opts.freeShippingThreshold) * 100);
    const unlocked = remainingCents <= 0;

    const subtext = unlocked
      ? escapeHtml(opts.i18n.freeShippingUnlocked || "")
      : (opts.i18n.freeShippingRemainingHtml || "").replace(
          /\{\{\s*amount\s*\}\}/,
          formatPrice(remainingCents, cart.currency)
        );

    return (
      '<div class="spi_cart-drawer-shipping-bar" id="spi_cart-drawer-shipping-bar">' +
      '<div class="spi_cart-drawer-shipping-progress-track">' +
      '<div class="spi_cart-drawer-shipping-progress-fill" style="width: ' + percent + '%;"></div>' +
      "</div>" +
      '<p class="spi_cart-drawer-shipping-subtext">' + subtext + "</p>" +
      "</div>"
    );
  }

  function renderCartAddon(cart) {
    if (!opts.enableCartAddon) return "";

    const checked = (cart.note || "").indexOf("Add-on:") !== -1;
    const title = escapeHtml(opts.i18n.addonTitle || "");

    return (
      '<div class="spi_cart-drawer-addon">' +
      '<input type="checkbox" id="spi_cart-drawer-gift-checkbox" data-addon-title="' + title + '"' + (checked ? " checked" : "") + ">" +
      '<label for="spi_cart-drawer-gift-checkbox">' + title + "</label>" +
      "</div>"
    );
  }

  function renderPromoCode(cart) {
    if (!opts.showPromoCode) return "";

    const discountApplications = cart.cart_level_discount_applications || [];
    const savedHtml = cart.total_discount > 0
      ? '<div class="spi_cart-drawer-promo-saved-msg">' +
        escapeHtml(opts.i18n.discount || "") + ": -" + formatPrice(cart.total_discount, cart.currency) +
        "</div>"
      : "";
    const appliedHtml = discountApplications.length
      ? '<div class="spi_cart-drawer-promo-applied">' +
        '<div class="spi_cart-drawer-promo-applied-row">' +
        '<div class="spi_cart-drawer-promo-applied-badge">' +
        "<span>" + escapeHtml(discountApplications.map(function (d) { return d.title; }).join(", ")) + "</span>" +
        "</div>" +
        '<button type="button" class="spi_cart-drawer-remove-btn" id="spi_cart-drawer-promo-remove">' +
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="m4.5 4.5 11 11m0-11-11 11"/></svg>' +
        "<span>" + escapeHtml(opts.i18n.remove || "") + "</span>" +
        "</button>" +
        "</div>" +
        savedHtml +
        "</div>"
      : "";

    const isExpanded = promoExpandedByUser == null ? discountApplications.length > 0 : promoExpandedByUser;

    return (
      '<div class="spi_cart-drawer-addon spi_cart-drawer-accordion-item spi_cart-drawer-promo' + (isExpanded ? " active" : "") + '">' +
      '<button type="button" class="spi_cart-drawer-accordion-header" id="spi_cart-drawer-promo-toggle" aria-expanded="' + isExpanded + '">' +
      '<span class="spi_cart-drawer-gift-label">' + escapeHtml(opts.i18n.promoCodeTitle || "") + "</span>" +
      '<span class="spi_cart-drawer-accordion-icon">' + (isExpanded ? "−" : "+") + "</span>" +
      "</button>" +
      '<div class="spi_cart-drawer-accordion-body">' +
      '<div class="spi_cart-drawer-accordion-body-inner">' +
      '<form class="spi_cart-drawer-promo-form" id="spi_cart-drawer-promo-form" data-root-url="' + escapeHtml(opts.rootUrl) + '" data-cart-url="' + escapeHtml(opts.cartUrl) + '" data-empty-code-message="' + escapeHtml(opts.i18n.promoCodeRequired || "") + '" data-invalid-code-message="' + escapeHtml(opts.i18n.promoCodeInvalid || "") + '" data-already-applied-message="' + escapeHtml(opts.i18n.promoCodeAlreadyApplied || "") + '">' +
      '<input type="text" class="spi_cart-drawer-promo-input" id="spi_cart-drawer-promo-input" placeholder="' + escapeHtml(opts.i18n.promoPlaceholder || "") + '">' +
      '<button type="submit" class="spi_cart-drawer-promo-btn" id="spi_cart-drawer-promo-btn">' +
      '<span class="spi_cart-drawer-promo-btn-text">' + escapeHtml(opts.i18n.apply || "") + "</span>" +
      '<span class="spi_cart-drawer-promo-btn-spinner" hidden aria-hidden="true"></span>' +
      "</button>" +
      "</form>" +
      appliedHtml +
      '<div class="spi_cart-drawer-promo-message" id="spi_cart-drawer-promo-msg" aria-live="polite"></div>' +
      "</div>" +
      "</div>" +
      "</div>"
    );
  }

  function renderCartNote(cart) {
    if (!opts.showCartNote) return "";

    const noteValue = escapeHtml(cart.note || "");
    const title = escapeHtml(opts.i18n.giftNoteTitle || "Order note");
    const placeholder = escapeHtml(opts.i18n.giftNotePlaceholder || "Special instructions for your order");

    return (
      '<div class="spi_cart-drawer-addon spi_cart-drawer-note">' +
      '<div class="spi_cart-drawer-gift-label">' + title + "</div>" +
      '<textarea class="spi_cart-drawer-note-textarea" id="spi_cart-drawer-note-textarea" placeholder="' + placeholder + '">' + noteValue + "</textarea>" +
      "</div>"
    );
  }

  function renderPriceBreakdown(cart) {
    const discountHtml = cart.total_discount > 0
      ? '<div class="spi_cart-drawer-subtotal spi_cart-drawer-discount-row">' +
        '<span class="spi_cart-drawer-subtotal-label">' + escapeHtml(opts.i18n.discount || "") + "</span>" +
        '<span class="spi_cart-drawer-subtotal-value">-' + formatPrice(cart.total_discount, cart.currency) + "</span>" +
        "</div>"
      : "";

    return (
      '<div class="spi_cart-drawer-subtotal">' +
      '<span class="spi_cart-drawer-subtotal-label">' + escapeHtml(opts.i18n.subtotal) + "</span>" +
      '<span class="spi_cart-drawer-subtotal-value">' + formatPrice(cart.original_total_price, cart.currency) + "</span>" +
      "</div>" +
      discountHtml +
      '<div class="spi_cart-drawer-subtotal spi_cart-drawer-total-row">' +
      '<span class="spi_cart-drawer-subtotal-label">' + escapeHtml(opts.i18n.total || "") + "</span>" +
      '<span class="spi_cart-drawer-subtotal-value">' + formatPrice(cart.total_price, cart.currency) + "</span>" +
      "</div>"
    );
  }

  function renderFooter(cart) {
    if (cart.item_count === 0) return "";

    return (
      renderPromoCode(cart) +
      renderCartNote(cart) +
      renderCartAddon(cart) +
      renderPriceBreakdown(cart) +
      /* Built in Liquid (snippets/cart-drawer.liquid) from translated,
         merchant-controlled strings — safe to insert as HTML. */
      (opts.i18n.taxNoteHtml ? '<p class="spi_cart-drawer-tax-note">' + opts.i18n.taxNoteHtml + "</p>" : "") +
      '<div class="spi_cart-drawer-actions">' +
      '<a href="' + opts.cartUrl + '/checkout" class="spi_btn-primary" id="spi_cart-drawer-checkout-btn"><span>' + escapeHtml(opts.i18n.checkout) + "</span></a>" +
      "</div>"
    );
  }

  function updateBadgeFromCart(cart) {
    document.querySelectorAll("#spi_cart-count, .spi_cart-count, .spi_cart-drawer-badge-count").forEach(function (badge) {
      badge.textContent = cart.item_count;
      badge.classList.toggle("visible", cart.item_count > 0);
    });
    const countLabel = document.getElementById("spi_cart-drawer-count");
    if (countLabel) countLabel.textContent = "(" + cart.item_count + ")";
  }

  async function fetchCart() {
    const res = await fetch(window.routes.cart_url + '.js');
    return res.json();
  }

  async function updateBadge() {
    try {
      updateBadgeFromCart(await fetchCart());
    } catch (err) {
      /* Network error updating badge; ignore, next full page load will resync. */
    }
  }

  async function refreshDrawer() {
    try {
      const cart = await fetchCart();

      if (shipping) shipping.innerHTML = renderShippingBar(cart);

      body.innerHTML = renderBody(cart);

      const footerHtml = renderFooter(cart);
      footer.innerHTML = footerHtml;

      updateBadgeFromCart(cart);
    } catch (err) {
      /* Network error refreshing drawer contents; ignore. */
    }
  }

  function setRowLoading(item, isLoading) {
    if (!item) return;
    item.classList.toggle("is-loading", isLoading);
    const qty = item.querySelector(".spi_cart-drawer-qty");
    if (qty) qty.hidden = isLoading;

    let spinner = item.querySelector(".spi_cart-drawer-item-spinner");
    if (isLoading) {
      if (!spinner) {
        spinner = document.createElement("span");
        spinner.className = "spi_cart-drawer-item-spinner";
        spinner.setAttribute("aria-hidden", "true");
        if (qty) qty.insertAdjacentElement("afterend", spinner);
      }
      spinner.hidden = false;
    } else if (spinner) {
      spinner.hidden = true;
    }
  }

  async function changeLine(lineId, quantity) {
    if (!lineId) return;
    const res = await fetch(window.routes.cart_change_url + '.js', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineId, quantity: quantity })
    });
    const data = await res.json().catch(function () { return {}; });

    // More than the available stock: Shopify either rejects the change
    // (422 + message) or caps the line at what's in stock. Tell the shopper
    // either way instead of silently snapping the quantity back.
    let message = "";
    if (!res.ok) {
      message = data.description || data.message || "";
    } else if (quantity > 0 && Array.isArray(data.items)) {
      const line = data.items.find(function (i) { return i.key === lineId; });
      if (line && line.quantity < quantity) {
        message = (opts.i18n.quantityLimit || "").replace("[quantity]", line.quantity);
      }
    }

    // refreshDrawer() re-renders body.innerHTML from scratch, which tears
    // down the row (and its loading spinner) we marked below — no manual
    // cleanup needed on success. isUpdating is still reset by the caller's
    // finally() so a failed request doesn't leave the drawer stuck.
    await refreshDrawer();
    if (message) showLineError(lineId, message);
  }

  function showLineError(lineId, message) {
    const row = Array.from(body.querySelectorAll(".spi_cart-drawer-item")).find(function (li) {
      return li.dataset.lineId === lineId;
    });
    if (!row) return;
    const details = row.querySelector(".spi_cart-drawer-item-details") || row;
    const error = document.createElement("p");
    error.className = "spi_cart-drawer-item-error";
    error.setAttribute("role", "alert");
    error.textContent = message;
    details.appendChild(error);
  }

  const CART_ADDON_NOTE_LINE_PATTERN = /^Add-on: .*$/m;

  async function setCartAddon(checkbox) {
    const cart = await fetchCart();
    let note = (cart.note || "").replace(CART_ADDON_NOTE_LINE_PATTERN, "").trim();
    if (checkbox.checked) {
      const line = "Add-on: " + checkbox.dataset.addonTitle;
      note = note ? note + "\n" + line : line;
    }
    await fetch(window.routes.cart_update_url + '.js', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: note })
    });
  }

  async function addToCart(form) {
    const formData = new FormData(form);
    // Let other scripts (e.g. the product section) mirror a loading state on
    // their own add-to-cart button while this request is in flight.
    document.dispatchEvent(new CustomEvent("spi:cart:adding", { detail: { form: form } }));
    try {
      const res = await fetch(window.routes.cart_add_url + '.js', {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData
      });
      if (res.ok) {
        await refreshDrawer();
        openDrawer(true);
      } else {
        // Refused add (e.g. more than the available stock): pass Shopify's
        // message to the page that submitted, so it can show it inline.
        let message = "";
        let errors = null;
        try {
          const data = await res.json();
          message = data.description || data.message || "";
          // Per-field errors (e.g. gift card recipient email) when Shopify sends them.
          errors = data.errors && typeof data.errors === "object" ? data.errors : null;
        } catch (e) {
          /* Non-JSON error body; the listener falls back to its own text. */
        }
        document.dispatchEvent(
          new CustomEvent("spi:cart:error", { detail: { form: form, message: message, errors: errors } })
        );
      }
    } finally {
      document.dispatchEvent(new CustomEvent("spi:cart:added", { detail: { form: form } }));
    }
  }

  if (closeBtn) closeBtn.addEventListener("click", closeDrawer);
  if (overlay) overlay.addEventListener("click", closeDrawer);

  let isUpdatingLine = false;

  body.addEventListener("click", function (e) {
    const decrease = e.target.closest("[data-qty-decrease]");
    const increase = e.target.closest("[data-qty-increase]");
    const remove = e.target.closest(".spi_cart-drawer-remove-btn");
    const trigger = decrease || increase || remove;
    if (!trigger || isUpdatingLine) return;

    const item = trigger.closest(".spi_cart-drawer-item");
    if (!item) return;

    let next = 0;
    if (decrease || increase) {
      const valueEl = item.querySelector("[data-qty-value]");
      const current = parseInt(valueEl.textContent, 10) || 0;
      next = increase ? current + 1 : Math.max(0, current - 1);
    }

    isUpdatingLine = true;
    setRowLoading(item, true);
    changeLine(item.dataset.lineId, next).finally(function () {
      isUpdatingLine = false;
    });
  });

  async function saveNote(noteText) {
    try {
      await fetch(window.routes.cart_update_url + '.js', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteText })
      });
    } catch (err) {}
  }

  function setPromoBtnLoading(btn, isLoading) {
    if (!btn) return;
    btn.disabled = isLoading;
    const text = btn.querySelector(".spi_cart-drawer-promo-btn-text");
    const spinner = btn.querySelector(".spi_cart-drawer-promo-btn-spinner");
    if (text) text.hidden = isLoading;
    if (spinner) spinner.hidden = !isLoading;
  }

  /**
   * Applying a discount code goes through Shopify's /discount/[code] GET
   * endpoint, which sets the code on the session and 302s to ?redirect=.
   * That doesn't require a *visible* page navigation, though — fetching it
   * in the background still lets the browser process the Set-Cookie/session
   * write (and follow the redirect internally), so we can apply the code
   * without ever leaving the drawer.
   */
  async function applyPromoCode(promoForm) {
    const input = document.getElementById("spi_cart-drawer-promo-input");
    const promoMsg = document.getElementById("spi_cart-drawer-promo-msg");
    const applyBtn = promoForm.querySelector(".spi_cart-drawer-promo-btn");
    const code = input && input.value.trim();

    if (!code) {
      if (promoMsg) {
        promoMsg.textContent = promoForm.dataset.emptyCodeMessage || "";
        promoMsg.classList.add("spi_cart-drawer-promo-message--error");
      }
      return;
    }

    if (promoMsg) {
      promoMsg.textContent = "";
      promoMsg.classList.remove("spi_cart-drawer-promo-message--error");
    }

    try {
      const existingCart = await fetchCart();
      const alreadyApplied = (existingCart.discount_codes || []).some(function (d) {
        return d.code.toLowerCase() === code.toLowerCase() && d.applicable;
      });
      if (alreadyApplied) {
        if (promoMsg) promoMsg.textContent = fillPromoMessage(promoForm.dataset.alreadyAppliedMessage, code);
        return;
      }
    } catch (err) {
      /* If this pre-check fails, fall through and let the apply attempt
         itself determine success/failure below. */
    }

    setPromoBtnLoading(applyBtn, true);

    const rootUrl = promoForm.dataset.rootUrl || "/";
    const discountUrl = rootUrl.replace(/\/$/, "") + "/discount/" + encodeURIComponent(code);

    try {
      await fetch(discountUrl, { credentials: "same-origin" });
      const cart = await fetchCart();
      const applied = (cart.discount_codes || []).some(function (d) {
        return d.code.toLowerCase() === code.toLowerCase() && d.applicable;
      });

      // refreshDrawer() re-renders the footer from scratch (including this
      // form), so grab the message/input again afterward rather than reuse
      // the ones captured above.
      await refreshDrawer();

      const newMsg = document.getElementById("spi_cart-drawer-promo-msg");
      const newInput = document.getElementById("spi_cart-drawer-promo-input");
      if (!applied && newMsg) {
        newMsg.textContent = fillPromoMessage(promoForm.dataset.invalidCodeMessage, code);
        newMsg.classList.add("spi_cart-drawer-promo-message--error");
      } else if (applied && newInput) {
        newInput.value = "";
      }
    } catch (err) {
      if (promoMsg) {
        promoMsg.textContent = fillPromoMessage(promoForm.dataset.invalidCodeMessage, code);
        promoMsg.classList.add("spi_cart-drawer-promo-message--error");
      }
      setPromoBtnLoading(applyBtn, false);
    }
  }

  footer.addEventListener("change", function (e) {
    if (e.target.id === "spi_cart-drawer-gift-checkbox") {
      setCartAddon(e.target).then(refreshDrawer);
    } else if (e.target.id === "spi_cart-drawer-note-textarea") {
      saveNote(e.target.value);
    }
  });

  async function removeDiscountCode(btn) {
    btn.disabled = true;
    btn.style.opacity = "0.6";
    try {
      // An empty "discount" value clears every discount code currently
      // applied to the cart (Shopify doesn't support removing just one of
      // several stacked codes independently).
      await fetch(window.routes.cart_update_url + '.js', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ discount: "" })
      });
    } catch (err) {}
    await refreshDrawer();
  }

  // The open/close animation itself is pure CSS (grid-template-rows 0fr ->
  // 1fr on .spi_cart-drawer-accordion-body, see cart-drawer.css) — it tracks
  // the row's real content height natively, so it can't drift out of sync
  // the way a JS-measured or fixed max-height can. This just flips the flag
  // class that enables the transition.
  function animateAccordion(bodyEl) {
    if (!bodyEl) return;
    bodyEl.classList.add("spi_cart-drawer-accordion-animate");
  }

  footer.addEventListener("click", async function (e) {
    const promoToggle = e.target.closest("#spi_cart-drawer-promo-toggle");
    if (promoToggle) {
      const item = promoToggle.closest(".spi_cart-drawer-promo");
      const bodyEl = item.querySelector(".spi_cart-drawer-accordion-body");
      const icon = promoToggle.querySelector(".spi_cart-drawer-accordion-icon");
      const nowExpanded = !item.classList.contains("active");
      animateAccordion(bodyEl);
      item.classList.toggle("active", nowExpanded);
      promoToggle.setAttribute("aria-expanded", String(nowExpanded));
      if (icon) icon.textContent = nowExpanded ? "−" : "+";
      promoExpandedByUser = nowExpanded;
      return;
    }

    const checkoutBtn = e.target.closest("#spi_cart-drawer-checkout-btn, .spi_cart-drawer-actions a");
    if (checkoutBtn) {
      e.preventDefault();
      const noteEl = document.getElementById("spi_cart-drawer-note-textarea");
      if (noteEl) {
        checkoutBtn.style.opacity = "0.7";
        checkoutBtn.style.pointerEvents = "none";
        await saveNote(noteEl.value);
      }
      window.location.href = opts.cartUrl + "/checkout";
      return;
    }

    const removeDiscountBtn = e.target.closest("#spi_cart-drawer-promo-remove");
    if (removeDiscountBtn) {
      removeDiscountCode(removeDiscountBtn);
    }
  });

  document.addEventListener("submit", function (e) {
    if (e.target.id === "spi_cart-drawer-promo-form") {
      e.preventDefault();
      applyPromoCode(e.target);
      return;
    }

    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form || !isDrawerMode()) return;
    e.preventDefault();
    addToCart(form);
  });

  window.SpiCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer,
    updateBadge: updateBadge,
    // Theme settings › Cart type: "drawer" or "page".
    isDrawerMode: isDrawerMode,
    hookHeaderCartIcon: function () {
      document.querySelectorAll("#spi_cart-toggle, .spi_cart-trigger").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          if (isDrawerMode()) {
            e.preventDefault();
            // Just viewing the cart -- nothing changed since the last
            // render (the static markup on first load, or whatever
            // refreshDrawer() last produced), so there's nothing to
            // re-fetch. See the comment in openDrawer() for why this
            // matters.
            openDrawer(true);
          }
        });
      });
    }
  };

  window.SpiCart.hookHeaderCartIcon();
  updateBadge();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSpiCartDrawer);
} else {
  initSpiCartDrawer();
}
